import { forwardRef, type FormEvent } from "react";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { clsx } from "clsx";

/**
 * Editorial input — mirrors the SearchBar pattern (`rounded-full` pill,
 * `border-rule` → `border-ink-info` on focus, `ring-2 ring-ink-info/20`
 * halo, leading MagnifyingGlass, optional inline submit pill). One
 * input vocabulary across every entrance + foot bar in the app.
 */
export interface EditorialInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  ariaLabel: string;
  /** "default" (Query entrance hero) | "compact" (Graph foot bar). */
  size?: "default" | "compact";
  /** Submit-button label. When set, an inline pill button renders on
   * the right (hidden < sm). Omit for icon-only submit. */
  submitLabel?: string;
  className?: string;
  autoFocus?: boolean;
}

export const EditorialInput = forwardRef<HTMLInputElement, EditorialInputProps>(
  function EditorialInput(
    {
      value,
      onChange,
      onSubmit,
      placeholder,
      ariaLabel,
      size = "default",
      submitLabel = "Ask",
      className,
      autoFocus,
    },
    ref,
  ) {
    const isCompact = size === "compact";
    function handleSubmit(e: FormEvent<HTMLFormElement>) {
      e.preventDefault();
      onSubmit();
    }
    return (
      <form
        onSubmit={handleSubmit}
        role="search"
        className={clsx("w-full", className)}
      >
        <div
          className={clsx(
            "flex w-full items-center gap-3 rounded-full border border-rule bg-surface-card shadow-sm transition",
            "focus-within:border-ink-info focus-within:ring-2 focus-within:ring-ink-info/20",
            isCompact ? "px-4 py-2" : "px-5 py-3",
          )}
        >
          <MagnifyingGlass
            size={isCompact ? 16 : 20}
            weight="regular"
            aria-hidden
            className="shrink-0 text-ink-tertiary"
          />
          <input
            ref={ref}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
            spellCheck={false}
            aria-label={ariaLabel}
            className={clsx(
              "w-full bg-transparent text-ink-primary placeholder:text-ink-tertiary focus:outline-none",
              isCompact ? "text-meta" : "text-ui",
            )}
          />
          <button
            type="submit"
            disabled={!value.trim()}
            aria-label={submitLabel}
            className={clsx(
              "inline-flex shrink-0 items-center justify-center rounded-full font-medium transition",
              "bg-ink-info text-surface-card hover:bg-ink-primary",
              "disabled:bg-rule disabled:text-ink-tertiary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-info focus-visible:ring-offset-2",
              // Labelled pill on >=sm; a tappable circular icon button on mobile
              // so the submit action is never hidden.
              isCompact
                ? "h-8 min-w-8 px-2.5 text-meta sm:px-3"
                : "h-9 min-w-9 px-3 text-meta sm:px-4",
            )}
          >
            <span className="hidden sm:inline">{submitLabel}</span>
            <ArrowRight
              size={isCompact ? 15 : 17}
              weight="bold"
              aria-hidden
              className="sm:hidden"
            />
          </button>
        </div>
      </form>
    );
  },
);
